import { ArrowRight, Factory, MapPin, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Location } from '@/contexts/LocationContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getProductionCentres } from '../utils/production-centres';

interface ProductionCentrePickerProps {
  locations: Location[];
  isLoading: boolean;
  onSelect: (location: Location) => void;
}

function CentreCard({ location, onSelect }: { location: Location; onSelect: (location: Location) => void }) {
  const { t } = useTranslation('production');
  const detail = [location.city, location.country_code].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={() => onSelect(location)}
      aria-label={t('centrePicker.continueNamed', { name: location.name })}
      className="group relative min-h-44 overflow-hidden rounded-xl border border-border/80 bg-card p-5 text-left shadow-sm transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-threadheads-purple/45 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple focus-visible:ring-offset-2"
    >
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-threadheads-purple via-violet-400 to-fuchsia-400 opacity-70 transition-opacity group-hover:opacity-100"
      />
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-threadheads-purple/10 text-threadheads-purple transition-colors group-hover:bg-threadheads-purple group-hover:text-white">
            <Factory className="h-6 w-6" aria-hidden="true" />
          </span>
          <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
            <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
            {t('centrePicker.active')}
          </Badge>
        </div>

        <div className="mt-5">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">{location.name}</h2>
          <p className="mt-1 flex min-h-5 items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
            {detail || t('centrePicker.locationUnavailable')}
          </p>
        </div>

        <span className="mt-auto flex items-center gap-2 pt-5 text-sm font-semibold text-threadheads-purple">
          {t('centrePicker.continue')}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </span>
      </div>
    </button>
  );
}

export function ProductionCentrePicker({ locations, isLoading, onSelect }: ProductionCentrePickerProps) {
  const { t } = useTranslation('production');
  const centres = getProductionCentres(locations);

  return (
    <div className="container mx-auto max-w-5xl px-6 py-10 sm:py-14">
      <section aria-labelledby="production-centre-heading" className="animate-enter">
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-threadheads-purple/10 text-threadheads-purple">
            <Factory className="h-7 w-7" aria-hidden="true" />
          </span>
          <h1 id="production-centre-heading" className="text-3xl font-semibold tracking-tight sm:text-4xl">
            {t('centrePicker.title')}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t('centrePicker.description')}
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2" aria-label={t('centrePicker.loading')}>
            {[0, 1].map((item) => (
              <Skeleton key={item} className="h-44 rounded-xl" />
            ))}
          </div>
        ) : centres.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {centres.map((centre) => (
              <CentreCard key={centre.id} location={centre} onSelect={onSelect} />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed bg-muted/20 px-6 py-10 text-center">
            <Factory className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden="true" />
            <h2 className="mt-4 text-lg font-semibold">{t('centrePicker.emptyTitle')}</h2>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">
              {t('centrePicker.emptyDescription')}
            </p>
            <Button asChild variant="outline" className="mt-5 h-12 gap-2">
              <Link to="/production/settings">
                <Settings className="h-4 w-4" aria-hidden="true" />
                {t('centrePicker.openSettings')}
              </Link>
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
